import clsx from 'clsx'
import { AnimatePresence, motion } from 'framer-motion'
import { t } from 'i18next'
import React, { useCallback, useMemo, useState } from 'react'
import { useEtracker } from '../../hooks/use-etracker'
import { Step, useStore } from '../../hooks/use-store'
import { CloseIcon } from '../../icons/close'
import { Button } from '../button/button'
import { TEXT_REVEAL_ANIMATION_DURATION } from '../text-reveal/text-reveal'
import styles from './exit-feedback-modal.module.css'
import { FinishedExitForm } from './forms/finished-exit-form'
import { InitialExitForm } from './forms/initial-exit-form'
import { JobDetailsExitForm } from './forms/job-details-exit-form'
import { NoMatchesExitForm } from './forms/no-matches-exit-form'
import { ProcessingExitForm } from './forms/processing-exit-form'

// Main ExitFeedbackModal component
export interface ExitFeedbackModalProps {
	step: Step
	onClose: () => void
}

export enum ExitFormNames {
	'ConsentCheck',
	'Processing',
	'PresentationOfMatches',
	'ResultDetails',
}

export const ExitFeedbackModal: React.FC<ExitFeedbackModalProps> = ({ step, onClose }) => {
	const { jobCardExpanded } = useStore()
	const { trackExitEvent } = useEtracker()
	const [open, setOpen] = useState(false)
	const [stepAtOpen, setStepAtOpen] = useState<Step>()
	const [jobCardExpandedAtOpen, setJobCardExpandedAtOpen] = useState<boolean>()

	const handleSubmit = useCallback(() => {
		onClose()
	}, [onClose])

	const handleDontParticipateClick = () => {
		trackExitEvent(t('exitFeedback.quickExit'), 'Click')
		onClose()
	}

	const handleOpen = () => {
		if (open) {
			return
		}

		if (step === Step.Error) {
			onClose()
			return
		}

		setOpen(true)
		setStepAtOpen(step)
		setJobCardExpandedAtOpen(jobCardExpanded)
	}

	const form = useMemo(() => {
		// Create an exit button that can be positioned alongside the submit button
		const exitButtonSlot = (
			<Button type={'text'} className={styles['quick-exit']} onClick={handleDontParticipateClick}>
				{t('exitFeedback.quickExit')}
			</Button>
		)

		switch (stepAtOpen) {
			case Step.Initial:
				return <InitialExitForm onSubmit={handleSubmit} exitButtonSlot={exitButtonSlot} />
			case Step.Processing:
			case Step.Preselecting:
			case Step.Evaluating:
			case Step.Selecting:
				return <ProcessingExitForm onSubmit={handleSubmit} exitButtonSlot={exitButtonSlot} />
			case Step.Finished:
				return jobCardExpandedAtOpen ? (
					<JobDetailsExitForm onSubmit={handleSubmit} exitButtonSlot={exitButtonSlot} />
				) : (
					<FinishedExitForm onSubmit={handleSubmit} exitButtonSlot={exitButtonSlot} />
				)
			case Step.NoResults:
				return <NoMatchesExitForm onSubmit={handleSubmit} exitButtonSlot={exitButtonSlot} />
		}
	}, [stepAtOpen, jobCardExpandedAtOpen, handleSubmit, handleDontParticipateClick])

	const feedbackNoteText = useMemo(() => {
		if (stepAtOpen === Step.Finished && jobCardExpanded) {
			return t('exitFeedback.formJobDetails.note')
		}
		return t('exitFeedback.note.text')
	}, [stepAtOpen, jobCardExpanded])

	return (
		<AnimatePresence>
			<motion.div
				className={clsx(styles['exit-modal'], {
					'exit-modal--open': open,
				})}
				layout
				transition={{
					layout: { duration: open ? 0.3 : 0, ease: 'easeInOut' },
					height: { duration: open ? 0.3 : 0, ease: 'easeInOut' },
				}}
			>
				{open && <motion.div className={styles.backdrop} onClick={() => setOpen(false)} />}
				<motion.div
					layout
					className={open ? styles.content : clsx(styles.close)}
					onClick={handleOpen}
					transition={{
						layout: { duration: open ? 0.3 : 0, ease: 'easeInOut' },
					}}
					initial={!open ? { opacity: 0 } : false}
					animate={
						!open
							? {
									opacity: 1,
									transition: {
										delay: TEXT_REVEAL_ANIMATION_DURATION,
									},
								}
							: { opacity: 1 }
					}
				>
					{!open ? (
						<AnimatePresence>
							<motion.div
								className={clsx(styles['close-animation-wrapper'], 'bold')}
								initial={{ opacity: 0 }}
								animate={{
									opacity: 1,
									transition: {
										duration: 0.5,
										delay: 0.25,
									},
								}}
								exit={{ opacity: 0 }}
							>
								{t('modal.close')}
								<CloseIcon />
							</motion.div>
						</AnimatePresence>
					) : (
						<>
							<button className={styles.back} onClick={() => setOpen(false)}>
								<CloseIcon />
							</button>
							<motion.div
								className={styles.header}
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								transition={{ delay: 0.1 }}
							>
								<motion.div className={styles.overline}>{t('exitFeedback.overline')}</motion.div>
								<h2 className={clsx(styles['exit-title'], 'bold')}>{t('exitFeedback.title')}</h2>
							</motion.div>
							<motion.div
								className={styles.note}
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								transition={{ delay: 0.2 }}
							>
								<h4 className={'bold'}> {t('exitFeedback.note.title')}</h4>
								<p>{feedbackNoteText}</p>
							</motion.div>
							<motion.div
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								transition={{ delay: 0.5 }}
								className={styles['form-animation-wrapper']}
							>
								<>{form}</>
							</motion.div>
						</>
					)}
				</motion.div>
			</motion.div>
		</AnimatePresence>
	)
}
